<template>
    <main>
        <b-alert
            :show="!id && $gate.deny('create', 'project')"
            variant="warning"
        >
            {{ $t('You are not allowed to create projects. To view a project, select one from the list') }}
        </b-alert>

      <header class="justify-between align-items-stretch mb-3">
            <b-breadcrumb
                v-if="breadcrumb.length"
                :items="breadcrumb"
                class="flex-grow-1 mb-0 mr-3"
            />

            <b-dropdown
                :id="'project_actions_dropdown_' + id"
                :title="$t('Actions')"
                class="ml-auto"
                no-caret
                right
            >
                <template slot="button-content">
                    <fa fixed-width icon="ellipsis-v" />
                </template>

                <b-dropdown-item
                    v-if="id && $gate.allow('assign_disabilities', item)"
                    v-b-modal.disabilitiesModal
                >
                    <fa fixed-width icon="exclamation" />
                    {{ $t('Assign disabilities') }}
                </b-dropdown-item>

                <b-dropdown-item
                    v-if="id && $gate.allow('drop_disabilities', item)"
                    v-b-modal.dropDisabilitiesModal
                >
                    <fa fixed-width icon="check" />
                    {{ $t('Drop disabilities') }}
                </b-dropdown-item>

                <b-dropdown-divider class="divider" />

                <complete-services
                    v-if="id && $gate.allow('complete_services', item)"
                    :api-url="`/api/projects/${id}/services_to_complete`"
                    :project-id="+id"
                    hide-service-select
                    :lazy="false"
                >
                    <template #activator="{toggle}">
                        <b-dropdown-item @click="toggle">
                            <fa fixed-width icon="check" />
                            {{ $t('Complete services') }}
                        </b-dropdown-item>
                    </template>
                </complete-services>

              <b-dropdown-divider class="divider" />

                <b-dropdown-item @click="$refs.childView.fetchData && $refs.childView.fetchData()">
                    <fa fixed-width icon="sync" />
                    {{ $t('Refresh') }}
                </b-dropdown-item>

                <b-dropdown-item-button
                    v-if="$gate.allow('duplicate', item)"
                    v-b-modal.duplicate-modal
                    :disabled="item.busy"
                >
                    <fa fixed-width icon="copy" />
                    {{ $t('Duplicate') }}
                </b-dropdown-item-button>

                <b-dropdown-item
                    v-if="!item.is_locked && $gate.allow('lock', item)"
                    @click="lock"
                >
                  <fa fixed-width icon="lock" />
                  {{ $t('Lock project') }}
                </b-dropdown-item>
                <b-dropdown-item
                    v-else-if="$gate.allow('unlock', item)"
                    @click="unlock"
                >
                  <fa fixed-width icon="unlock" />
                  {{ $t('Unlock project') }}
                </b-dropdown-item>

                <b-dropdown-divider />

<!--                <b-dropdown-item-->
<!--                    v-if="id && $gate.allow('export', item)"-->
<!--                    @click="dispatchExportService"-->
<!--                >-->
<!--                    <fa fixed-width icon="file-export" />-->
<!--                    {{ $t('DA11 Export by Service') }}-->
<!--                </b-dropdown-item>-->

                <b-dropdown-item
                    v-if="id && $gate.allow('export', item)"
                    v-b-modal.da11ExportModal
                >
                    <fa fixed-width icon="file-export" />
                    {{ $t('DA11 Export') }}
                </b-dropdown-item>

<!--				<b-dropdown-item-->
<!--					v-if="id && $gate.allow('export', item)"-->
<!--					@click="dispatchExportProject"-->
<!--				>-->
<!--					<fa fixed-width icon="file-export" />-->
<!--					{{ $t('DA11 Export by Project') }}-->
<!--				</b-dropdown-item>-->

                <b-dropdown-divider />

                <b-dropdown-item
                    v-if="id && $gate.allow('update', item)"
                    v-b-modal.uploadAttachmentModal
                >
                  <fa fixed-width icon="file-upload" />
                  {{ $t('Upload attachment') }}
                </b-dropdown-item>

                <b-dropdown-divider />

                <b-dropdown-item
                    v-if="id && $gate.allow('delete', item)"
                    variant="danger"
                    @click="handleDelete"
                >
                    <fa fixed-width icon="trash" />
                    {{ $t('Delete') }}
                </b-dropdown-item>
            </b-dropdown>
        </header>

      <b-alert
          :show="item.is_locked"
          variant="warning"
      >
        {{ $t('This project is locked by: {0}', [item.locked_by ? item.locked_by.name : $t('Unknown user')]) }}
      </b-alert>


      <template v-if="id">
            <assign-disabilities :project-id="+id" />

            <drop-disabilities :project-id="+id" />

            <da11-export :project-id="+id" :last-export="item.last_export_date" />

            <upload-attachment modal-id="uploadAttachmentModal" :project-id="+id" />

        <duplicate-modal
            v-if="$gate.allow('duplicate', item)"
            id="duplicate-modal"
            :api-url="duplicateUrl"
            @duplicated="handleDuplicated"
        >
          <template #default="{form}">
            <b-form-group
                :invalid-feedback="form.errors.get('name')"
                :label="$t('Name')"
                label-for="new-name"
            >
              <b-form-input
                  id="new-name"
                  v-model="form.name"
                  :state="form.errors.has('name') ? false : null"
                  required
              />
            </b-form-group>
          </template>

        </duplicate-modal>
        </template>


        <b-card no-body>
            <b-card-header v-if="id">
                <b-nav card-header tabs>
                    <b-nav-item :to="{name: 'projects.show', params: {projectId: id}}">
                        {{ $t('Measurement') }}
                    </b-nav-item>

                    <b-nav-item :to="{name: 'projects.planning', params: {projectId: id}}">
                        {{ $t('Planning') }}
                    </b-nav-item>

                    <b-nav-item :to="{name: 'projects.project-management', params: {projectId: id}}">
                        {{ $t('Project management') }}
                    </b-nav-item>

                    <b-nav-item :to="{name: 'projects.administration', params: {projectId: id}}">
                        {{ $t('Administration') }}
                    </b-nav-item>
                </b-nav>
            </b-card-header>

            <router-view ref="childView" :project="item" />
        </b-card>
    </main>
</template>

<script>

    import AssignDisabilities from '@/components/projects/AssignDisabilities'
    import DropDisabilities from "@/components/projects/DropDisabilities";
    import CompleteServices from "@/components/projects/CompleteServices";
    import Da11Export from "@/components/projects/Da11Export";
    import itemMixin from "@/mixins/itemMixin";
    import DuplicateModal from '@/components/DuplicateModal'

    import axios from 'axios'
    import UploadAttachment from "@/components/projects/UploadAttachment";

    export default {
        name: 'ProjectsShow',
        components: {UploadAttachment, CompleteServices, DropDisabilities, AssignDisabilities, Da11Export, DuplicateModal},
        mixins: [itemMixin],
        computed: {
            resourceName() {
                return `projects`;
            }
        },
		methods: {
			async lock() {
				await this.axios.post(`/api/projects/${this.id}/lock`)

				this.successToast(this.$t('Project locked'))

				this.fetchData();
			},

			async unlock() {
				await this.axios.post(`/api/projects/${this.id}/unlock`)

				this.successToast(this.$t('Project unlocked'))

				this.fetchData();
			},

      handleDuplicated(data) {
        this.$root.$emit('duplicated::projects', data)
      },
		},
	}
</script>
